// components/dashboard/UsageBanner.js
import React, { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, X, ArrowRight, Info } from 'lucide-react';

const UsageBanner = ({ api }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchUsage();
    
    // Refresh usage every 5 minutes
    const interval = setInterval(fetchUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchUsage = async () => {
    try {
      const data = await api.getUsage();
      setUsage(data);
      
      // Reset dismissed state if warning level changes
      if (data.warningLevel !== 'none' && data.warningLevel !== 'info') {
        setDismissed(false);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage || usage.unlimited) {
    return null;
  }

  // Don't show banner if dismissed or no warning
  if (dismissed || usage.warningLevel === 'none') {
    return null;
  }

  const getConfig = () => {
    const { warningLevel, percentageUsed, remaining, daysUntilLimit } = usage;
    
    const configs = {
      info: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: Info,
        iconBg: 'bg-blue-100',
        title: `You're ${Math.round(percentageUsed)}% through your monthly ticket limit`,
        message: `${remaining} tickets remaining this month. You're on track!`,
        showDismiss: true,
        showUpgrade: false
      },
      warning: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-800',
        icon: AlertTriangle,
        iconBg: 'bg-amber-100',
        title: 'Approaching your monthly ticket limit',
        message: `Only ${remaining} tickets remaining (${Math.round(percentageUsed)}% used). ${
          daysUntilLimit ? `At your current pace, you'll hit the limit in ~${daysUntilLimit} days.` : ''
        }`,
        showDismiss: true,
        showUpgrade: true
      },
      danger: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        icon: AlertTriangle,
        iconBg: 'bg-orange-100',
        title: 'Running low on tickets',
        message: `Only ${remaining} tickets left (${Math.round(percentageUsed)}% used). Consider upgrading to avoid interruptions.`,
        showDismiss: false,
        showUpgrade: true
      },
      critical: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        icon: AlertTriangle,
        iconBg: 'bg-red-100',
        title: 'Monthly ticket limit reached',
        message: remaining === 0 
          ? 'You cannot create new tickets until next billing cycle. Upgrade now for unlimited access.'
          : `Final ${remaining} tickets remaining. Upgrade to continue uninterrupted service.`,
        showDismiss: false,
        showUpgrade: true
      }
    };
    
    return configs[warningLevel] || configs.info;
  };

  const config = getConfig();
  const Icon = config.icon;

  return (
    <div className={`${config.bg} border-b ${config.border} px-6 py-3`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          <div className={`${config.iconBg} rounded-lg p-2 mr-3 flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${config.text}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${config.text}`}>
              {config.title}
            </p>
            <p className={`text-sm ${config.text} opacity-90`}>
              {config.message}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 ml-4">
          {/* Usage Bar */}
          <div className="hidden md:flex items-center gap-2">
            <div className="w-32 h-2 bg-white rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${
                  usage.percentageUsed >= 90 ? 'bg-red-500' :
                  usage.percentageUsed >= 80 ? 'bg-orange-500' :
                  usage.percentageUsed >= 50 ? 'bg-amber-500' :
                  'bg-blue-500'
                }`}
                style={{ width: `${Math.min(usage.percentageUsed, 100)}%` }}
              />
            </div>
            <span className={`text-sm font-medium ${config.text}`}>
              {usage.used}/{usage.limit}
            </span>
          </div>
          
          {/* Projection Badge */}
          {usage.projection?.projectedOverage && (
            <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-white rounded-md">
              <TrendingUp className="w-3 h-3 text-red-600" />
              <span className="text-xs font-medium text-red-600">
                Projected: {usage.projection.projectedTotal}
              </span>
            </div>
          )}
          
          {config.showUpgrade && (
            <button
              onClick={() => window.location.href = '/pricing'}
              className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 ${
                usage.warningLevel === 'critical' 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Upgrade Plan
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          
          {config.showDismiss && (
            <button
              onClick={() => setDismissed(true)}
              className={`p-1 rounded hover:bg-white/50 ${config.text}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageBanner;